import coupleCafe from './assets/couple-cafe.jpg'
import coupleClose from './assets/couple-close.jpg'
import coupleDay from './assets/couple-day.jpg'
import coupleKitchen from './assets/couple-kitchen.jpg'
import couplePatio from './assets/couple-patio.jpg'
import coupleWaterfront from './assets/couple-waterfront.jpg'
import coupleWine from './assets/couple-wine.jpg'
import heroMontage from './assets/hero-montage.mp4'
import rheyaPortrait from './assets/rheya-portrait.jpg'
import { urls } from './config'

/** Stable public URL so index.html can preload the LCP poster. */
const heroPoster = '/hero-poster.jpg'

/** Four frames only — club restraint over stock density */
const film = [
  {
    src: couplePatio,
    alt: 'A couple laughing together on an outdoor patio at golden hour',
  },
  {
    src: coupleWine,
    alt: 'A couple enjoying each other at a bar on a first date',
  },
  {
    src: coupleCafe,
    alt: 'A couple laughing over coffee on a daytime date',
  },
  {
    src: coupleDay,
    alt: 'A couple walking together holding hands in daylight',
  },
] as const

const connectionShots = [
  {
    src: coupleClose,
    alt: 'A couple leaning in close on a park bench',
    wide: false,
  },
  {
    src: coupleWaterfront,
    alt: 'A couple laughing while walking a waterfront',
    wide: true,
  },
  {
    src: coupleKitchen,
    alt: 'A couple cooking together and laughing',
    wide: false,
  },
] as const

const membership: {
  name: string
  title: string
  body: string
  emphasize?: boolean
}[] = [
  {
    name: 'Apply',
    title: 'Cross the threshold',
    body: 'Start an application when you want something real. The club only works if everyone here is intentional.',
  },
  {
    name: 'Interview',
    title: 'Talk with Rheya — then a yes or a no',
    body: 'Rheya interviews you for fit. Some people are welcomed into the club. Some aren’t. That gate is the product, not a formality.',
    emphasize: true,
  },
  {
    name: 'Introductions',
    title: 'Curated matches over time',
    body: 'Once you’re in, she keeps learning who you are and who you want. Introductions come when both sides fit — quality over volume.',
  },
]

const pledges = [
  {
    title: 'You’re open to a real relationship',
    body: 'Not hunting for casual by default. You want a partner, or the honest possibility of one.',
  },
  {
    title: 'You’ll sit for an interview',
    body: 'Rheya evaluates fit for the club. That’s how the room stays worth being in.',
  },
  {
    title: 'The room is the product',
    body: 'Standards apply to everyone — including you. Who else is admitted is the whole point.',
  },
] as const

/**
 * Matching chart: real values, crossing check, one side fails.
 * Self ≠ accept, both directions scored, weight decides hard fails.
 */
const crossCheck = {
  topic: 'One topic: drinking',
  you: {
    self: 'Doesn’t drink',
    accept: 'Doesn’t drink · drinks socially',
    weight: 'Flexible',
    verdict: 'Clears',
  },
  them: {
    self: 'Drinks socially',
    accept: 'Drinks socially',
    weight: 'Must-have',
    verdict: 'Doesn’t clear',
  },
} as const

const matchMechanics = [
  {
    title: 'Two records per domain — never one',
    body: 'Across intent, kids, substances, politics, faith, and interests, Rheya keeps who you are and who you’ll accept as separate records. On the domains where it matters most — kids, intent, substances, faith — she also keeps how hard a line it is. “I don’t smoke; my partner can drink, but not smoke” is a stored record, not a vibe tag.',
  },
  {
    title: 'Both directions clear the bar, separately',
    body: 'Your side and their side are totalled independently across every shared domain, and each total has to clear a floor on its own. An average can hide a match that only really works for one of you. Here it doesn’t — a good score one way can’t carry a weak one the other way.',
  },
  {
    title: 'Domains stack; thin data stays honest',
    body: 'Only the domains you’ve both completed count toward a pair. The more you share, the more the score is allowed to claim. With one or two, it’s deliberately pulled back toward a coin flip — we’d rather show less confidence than manufacture it.',
  },
] as const

export const LandingPage = () => {
  return (
    <div className="site">
      <a className="site__skip" href="#main">
        Skip to content
      </a>

      <header className="topbar">
        <div className="topbar__inner">
          <a className="topbar__brand" href="/" aria-label="Revel Club">
            <span className="topbar__mark" aria-hidden="true">
              <span className="topbar__mark-letter">R</span>
            </span>
            <span className="topbar__name">
              Revel <span className="topbar__name-club">Club</span>
            </span>
          </a>

          <input id="nav-toggle" className="topbar__toggle" type="checkbox" />
          <label className="topbar__burger" htmlFor="nav-toggle" aria-label="Open menu">
            <span />
            <span />
            <span />
          </label>

          <nav
            className="topbar__nav"
            aria-label="Primary"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('a')) {
                const toggle = document.getElementById('nav-toggle') as HTMLInputElement | null
                if (toggle) toggle.checked = false
              }
            }}
          >
            <a href="#membership">Membership</a>
            <a href="#matching">Matching</a>
            <a href="#connection">Dates</a>
            <a href="#rheya">Rheya</a>
            <a href="#who">Who it’s for</a>
            <a href={urls.blog}>Blog</a>
            <a className="topbar__ghost" href={urls.login}>
              Sign in
            </a>
            <a className="topbar__cta" href={urls.register}>
              Apply
            </a>
          </nav>
        </div>
      </header>

      <main id="main">
        <section className="intro" aria-labelledby="intro-heading">
          <div className="intro__media" aria-hidden="true">
            <video
              className="intro__video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={heroPoster}
              title="Revel Club — intentional dating"
            >
              <source src={heroMontage} type="video/mp4" />
            </video>
          </div>
          <div className="intro__veil" aria-hidden="true" />
          <div className="intro__grid">
            <div className="intro__copy">
              <p className="eyebrow">Private matchmaking club</p>
              <h1 id="intro-heading" className="intro__title">
                You’re not looking for more options.
                <span className="intro__title-line">You’re looking for the right person.</span>
              </h1>
              <p className="intro__lede">
                Revel Club is intentional dating with Rheya — the club’s AI matchmaker. She
                interviews you, holds a high bar for membership, and makes introductions only when
                both sides fit. A match, not a feed.
              </p>
              <div className="intro__actions">
                <a className="btn btn--gold" href={urls.register}>
                  Start your application
                </a>
                <a className="btn btn--quiet" href="#membership">
                  How membership works
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Proof band — Claude’s highest-leverage ask; keep light and honest */}
        <section className="proof" aria-label="How Revel is opening">
          <div className="proof__inner">
            <p className="proof__line">
              <strong>Matching is local.</strong> Regions open as interest grows; some cities start
              on a waitlist until there’s enough of a club to make introductions meaningful.
            </p>
            <p className="proof__line">
              <strong>Membership is free while you join</strong> — with a limited number of
              introductions per period. Paid tiers raise that capacity; the standard for who gets
              introduced stays the same.
            </p>
          </div>
        </section>

        <section className="panel panel--paper" aria-labelledby="problem-heading">
          <div className="panel__narrow">
            <p className="eyebrow eyebrow--on-paper">Why Revel</p>
            <h2 id="problem-heading" className="display display--on-paper">
              Swipe culture optimizes for volume. Matchmaking optimizes for fit.
            </h2>
            <p className="prose prose--on-paper">
              Most products treat introduction as a numbers game: more profiles, more messages, more
              noise. Revel is built the other way. You apply. You sit with Rheya. She decides if the
              club is right for you — and only later, who might be right for each other.
            </p>
            <ul className="contrast">
              <li>
                <span>A match, not a feed</span>
                Rheya runs the desk: interview, standards, introductions.
              </li>
              <li>
                <span>Mutual fit only</span>
                Compatibility isn’t “you like them.” It’s that you both make sense for each other.
              </li>
              <li>
                <span>The room is the product</span>
                Casual seekers are screened out. Who else is here is the whole point.
              </li>
            </ul>
          </div>
        </section>

        <section className="film" aria-label="What you’re applying for">
          <div className="film__head">
            <p className="eyebrow">What you’re applying for</p>
            <p className="film__note">
              Real dates — among members who already passed the same gate. Not more profiles.
            </p>
          </div>
          <ul className="film__track">
            {film.map((shot) => (
              <li key={shot.alt} className="film__shot">
                <figure>
                  <img
                    src={shot.src}
                    alt={shot.alt}
                    loading="lazy"
                    decoding="async"
                    width={960}
                    height={1200}
                  />
                </figure>
              </li>
            ))}
          </ul>
        </section>

        <section id="membership" className="panel" aria-labelledby="membership-heading">
          <div className="panel__wide">
            <header className="panel__header">
              <p className="eyebrow">Membership</p>
              <h2 id="membership-heading" className="display">
                How it works
              </h2>
            </header>
            <ol className="path">
              {membership.map((step, index) => (
                <li
                  key={step.name}
                  className={`path__step${step.emphasize ? ' path__step--gate' : ''}`}
                >
                  <span className="path__index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <p className="path__name">{step.name}</p>
                  <h3 className="path__title">{step.title}</h3>
                  <p className="path__body">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section
          id="matching"
          className="panel panel--paper matching"
          aria-labelledby="matching-heading"
        >
          <div className="panel__wide">
            <header className="matching__head">
              <div className="matching__intro">
                <p className="eyebrow eyebrow--on-paper">How matching works</p>
                <h2 id="matching-heading" className="display display--on-paper">
                  Not whether you’re similar. Whether each of you is what the other actually wants.
                </h2>
                <p className="lede lede--on-paper">
                  Most systems collapse you into one profile and hunt for lookalikes. Ours keeps two
                  records on every domain Rheya learns: who you are, and who you’ll accept. Scoring
                  is a cross-check — their life against your wants, your life against theirs.
                </p>
              </div>

              <figure className="match-flow">
                <p className="match-flow__topic">{crossCheck.topic}</p>

                <div
                  className="match-flow__diagram"
                  role="img"
                  aria-label={`Cross-check on drinking: your side clears because their “${crossCheck.them.self.toLowerCase()}” is on your accept list. Their side fails because your “${crossCheck.you.self.toLowerCase()}” is not on theirs, and they marked it a must-have.`}
                >
                  <div className="match-flow__nodes">
                    <article className="match-flow__node match-flow__node--you">
                      <p className="match-flow__seal">You</p>
                      <div className="match-flow__slot match-flow__slot--self">
                        <span className="match-flow__slot-label">I am</span>
                        <span className="match-flow__slot-value">{crossCheck.you.self}</span>
                      </div>
                      <div className="match-flow__slot match-flow__slot--accept">
                        <span className="match-flow__slot-label">I’ll accept</span>
                        <span className="match-flow__slot-value">{crossCheck.you.accept}</span>
                      </div>
                      <div className="match-flow__slot match-flow__slot--weight">
                        <span className="match-flow__slot-label">Weight</span>
                        <span className="match-flow__weight">{crossCheck.you.weight}</span>
                      </div>
                    </article>

                    <article className="match-flow__node match-flow__node--them">
                      <p className="match-flow__seal">Them</p>
                      <div className="match-flow__slot match-flow__slot--self">
                        <span className="match-flow__slot-label">I am</span>
                        <span className="match-flow__slot-value">{crossCheck.them.self}</span>
                      </div>
                      <div className="match-flow__slot match-flow__slot--accept">
                        <span className="match-flow__slot-label">I’ll accept</span>
                        <span className="match-flow__slot-value">{crossCheck.them.accept}</span>
                      </div>
                      <div className="match-flow__slot match-flow__slot--weight">
                        <span className="match-flow__slot-label">Weight</span>
                        <span className="match-flow__weight match-flow__weight--hard">
                          {crossCheck.them.weight}
                        </span>
                      </div>
                    </article>
                  </div>

                  <p className="match-flow__bridge-label">Cross-check — both directions</p>

                  <ol className="match-flow__tracks">
                    <li className="match-flow__track match-flow__track--pass">
                      <span className="match-flow__track-dir">Your direction</span>
                      <div className="match-flow__pipe">
                        <span className="match-flow__endpoint match-flow__endpoint--from">
                          <span className="match-flow__endpoint-kicker">Their life</span>
                          <span className="match-flow__endpoint-value">{crossCheck.them.self}</span>
                        </span>
                        <span className="match-flow__conduit" aria-hidden="true">
                          <span className="match-flow__conduit-bar" />
                          <span className="match-flow__conduit-head" />
                        </span>
                        <span className="match-flow__endpoint match-flow__endpoint--to">
                          <span className="match-flow__endpoint-kicker">Your wants</span>
                          <span className="match-flow__endpoint-value">Accept list</span>
                        </span>
                        <span className="match-flow__stamp match-flow__stamp--pass">
                          {crossCheck.you.verdict}
                        </span>
                      </div>
                    </li>

                    <li className="match-flow__track match-flow__track--fail">
                      <span className="match-flow__track-dir">Their direction</span>
                      <div className="match-flow__pipe">
                        <span className="match-flow__endpoint match-flow__endpoint--from">
                          <span className="match-flow__endpoint-kicker">Your life</span>
                          <span className="match-flow__endpoint-value">{crossCheck.you.self}</span>
                        </span>
                        <span className="match-flow__conduit" aria-hidden="true">
                          <span className="match-flow__conduit-bar" />
                          <span className="match-flow__conduit-head" />
                        </span>
                        <span className="match-flow__endpoint match-flow__endpoint--to">
                          <span className="match-flow__endpoint-kicker">Their wants</span>
                          <span className="match-flow__endpoint-value">Accept list</span>
                        </span>
                        <span className="match-flow__stamp match-flow__stamp--fail">
                          {crossCheck.them.verdict}
                        </span>
                      </div>
                    </li>
                  </ol>
                </div>

                <figcaption className="match-flow__caption">
                  Their answer is on your list, so your side clears. Yours isn’t on theirs — and
                  they marked it a must-have, so their side of this topic collapses. Every shared
                  topic is scored this way, and the two sides are totalled apart.
                </figcaption>
              </figure>
            </header>

            <ol className="match-laws">
              {matchMechanics.map((item, index) => (
                <li key={item.title} className="match-laws__item">
                  <span className="match-laws__index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="connection" className="connection" aria-labelledby="connection-heading">
          <div className="connection__intro">
            <p className="eyebrow">After the introduction</p>
            <h2 id="connection-heading" className="display">
              Dates that feel like dates
            </h2>
            <p className="lede">
              When the match is mutual, you get to show up as yourself — coffee, dinner, a walk,
              cooking at home. The point isn’t collecting chats. It’s whether two people click.
            </p>
          </div>
          <div className="connection__mosaic">
            {connectionShots.map((shot) => (
              <figure
                key={shot.alt}
                className={`connection__card${shot.wide ? ' connection__card--wide' : ''}`}
              >
                <img
                  src={shot.src}
                  alt={shot.alt}
                  loading="lazy"
                  decoding="async"
                  width={shot.wide ? 1600 : 960}
                  height={1200}
                />
              </figure>
            ))}
          </div>
        </section>

        <section id="rheya" className="meet" aria-labelledby="rheya-heading">
          <div className="meet__media">
            <img
              src={rheyaPortrait}
              alt="Portrait of Rheya, Revel Club’s AI matchmaker"
              width={1024}
              height={1008}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="meet__copy">
            <p className="eyebrow">The desk</p>
            <h2 id="rheya-heading" className="display">
              How Rheya works
            </h2>
            <blockquote className="meet__quote">
              “This isn’t a test you pass or fail. I’m trying to understand you — there’s no wrong
              answer, just an honest one.”
            </blockquote>
            <p className="prose">
              Rheya is a conversational AI with a matchmaker’s temperament — sharp, warm, and
              unsentimental about standards. She’s just talking with you — no forms to fill in.
            </p>
            <p className="prose">
              After you’re admitted, she reaches out when she has something worth saying. When you
              open chat for advice, she waits for you.
            </p>
          </div>
        </section>

        <section id="who" className="panel panel--paper" aria-labelledby="who-heading">
          <div className="panel__wide">
            <header className="panel__header">
              <p className="eyebrow eyebrow--on-paper">Who it’s for</p>
              <h2 id="who-heading" className="display display--on-paper">
                Who should apply
              </h2>
              <p className="lede lede--on-paper">Built for people who want a match, not a feed.</p>
            </header>
            <div className="pledges">
              {pledges.map((p) => (
                <article key={p.title} className="pledge">
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="panel" aria-labelledby="faq-heading">
          <div className="panel__narrow">
            <header className="panel__header">
              <p className="eyebrow">Before you apply</p>
              <h2 id="faq-heading" className="display">
                Straight answers
              </h2>
            </header>
            <dl className="faq">
              <div className="faq__item">
                <dt id="faq-rheya">Is Rheya a person?</dt>
                <dd role="definition" aria-labelledby="faq-rheya">
                  Rheya is the club’s AI matchmaker — a conversational agent with a defined
                  personality and process. She interviews, screens for intention, and helps form
                  introductions. She isn’t a human staffer behind a desk, and she isn’t a generic
                  chatbot bolted onto a swipe app.
                </dd>
              </div>
              <div className="faq__item">
                <dt id="faq-free">Is this free?</dt>
                <dd role="definition" aria-labelledby="faq-free">
                  Yes for core membership. Free members get up to 10 introductions per cycle, with
                  up to 8 active matches at a time (about a 4-day reset). Paid members get more
                  introductions and a shorter cycle. The quality bar for introductions doesn’t drop.
                </dd>
              </div>
              <div className="faq__item">
                <dt id="faq-where">Where does Revel operate?</dt>
                <dd role="definition" aria-labelledby="faq-where">
                  Matching is geographic. Regions open as local interest grows. If your city isn’t
                  live yet, you can join the waitlist when you apply — we’ll open introductions when
                  the room is full enough to matter.
                </dd>
              </div>
              <div className="faq__item">
                <dt id="faq-reject">What if I’m not accepted?</dt>
                <dd role="definition" aria-labelledby="faq-reject">
                  Not everyone is the right fit — and that’s intentional. Rejection protects the
                  members already inside. We won’t keep you in limbo forever.
                </dd>
              </div>
              <div className="faq__item">
                <dt id="faq-intros">How many introductions will I get?</dt>
                <dd role="definition" aria-labelledby="faq-intros">
                  Fewer than swipe apps, on purpose. Free members: up to 10 per cycle, 8 active max.
                  Paid members: up to 32 per cycle, 16 active max. Quality thresholds stay in place
                  so volume never becomes the goal.
                </dd>
              </div>
              <div className="faq__item">
                <dt id="faq-match">How does matching actually work?</dt>
                <dd role="definition" aria-labelledby="faq-match">
                  Not lookalikes — a two-way check. Who you are against what they’ll accept, and who
                  they are against what you’ll accept. Both sides have to clear. Detail in{' '}
                  <a href="#matching">How matching works</a>.
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="close" aria-labelledby="close-heading">
          <h2 id="close-heading" className="close__title">
            Apply if you want a match, not a feed
          </h2>
          <p className="close__body">
            Start an application. Talk with Rheya. If she welcomes you in, you’ll meet people who
            passed the same gate.
          </p>
          <div className="intro__actions intro__actions--center">
            <a className="btn btn--gold" href={urls.register}>
              Start your application
            </a>
            <a className="btn btn--quiet" href={urls.login}>
              Already a member? Sign in
            </a>
          </div>
        </section>
      </main>

      <footer className="foot">
        <div className="foot__inner">
          <p className="foot__copy">© {new Date().getFullYear()} Revel Club</p>
          <div className="foot__links">
            <a href={urls.blog}>Blog</a>
            <a href={urls.terms}>Terms</a>
            <a href={urls.privacy}>Privacy</a>
            <a href={urls.register}>Apply</a>
            <a href={urls.login}>Sign in</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
